import { useState, useMemo, useCallback, useEffect, memo } from 'react';
import { debounce } from 'debounce';

import Picture from 'types/Picture';
import Breakpoint from 'types/Breakpoint';
import PhotoSlider from '@templates/PhotoSlider';
import Modal from '@components/Modal';
import HtmlPreview from '@components/HtmlPreview';
import useBreakpoint from 'hooks/useBreakpoint';
import useMultiKeyPress, { areKeysPressed } from 'hooks/useMultiKeyPress';

import classes from './ProductPhoto.module.scss';

type Props = {
  id: number;
  mainTitle: string;
  productTypeId: number;
  photos: Picture[];
};

const ProductPhoto = ({ id, mainTitle, productTypeId, photos }: Props): JSX.Element => {
  const [selectedSlideIndex, setSelectedSlideIndex] = useState(0);
  const [selectedSmallSlideIndex, setSelectedSmallSlideIndex] = useState(0);
  const [lightBoxIsOpen, setLightBoxIsOpen] = useState(false);
  const breakpoint = useBreakpoint();
  const { keysPressed } = useMultiKeyPress();
  const slidesPerView = useMemo(() => {
    const breakpointProps: Record<Breakpoint, number> = {
      xLarge: 4,
      large: 4,
      largeMedium: 2,
      medium: 4,
      small: 1,
      xSmall: 1,
    };

    return Math.min(breakpoint ? breakpointProps[breakpoint] || 4 : 4, photos.length);
  }, [breakpoint, photos.length]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onSlideChange = useCallback(
    debounce((slideIndex: number) => {
      setSelectedSlideIndex(slideIndex);
      const slide = Math.floor(slideIndex / slidesPerView);
      setSelectedSmallSlideIndex(slide * slidesPerView);
    }, 100),
    [slidesPerView]
  );

  const onSlideClick = (slideIndex: number) => {
    setSelectedSlideIndex(slideIndex);
  };

  useEffect(() => {
    const isEscPressed = areKeysPressed(['Escape'], keysPressed);
    const isLeftArrowPressed = areKeysPressed(['ArrowLeft'], keysPressed);
    const isRightArrowPressed = areKeysPressed(['ArrowRight'], keysPressed);

    if (isEscPressed && lightBoxIsOpen) {
      setLightBoxIsOpen(false);
    }

    if (isLeftArrowPressed && selectedSlideIndex >= 1) {
      onSlideChange(selectedSlideIndex - 1);
    }

    if (isRightArrowPressed && selectedSlideIndex < photos.length - 1) {
      onSlideChange(selectedSlideIndex + 1);
    }
  }, [keysPressed]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedSlideIndex > photos.length - 1) {
      onSlideChange(0);
    }
  }, [photos.length]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={classes.wrapper}>
      <div className={classes.photosBox}>
        <PhotoSlider
          id={id}
          mainPhotoPreset="p_main"
          productTypeId={productTypeId}
          photos={photos}
          selectedSlideIndex={selectedSlideIndex}
          selectedSmallSlideIndex={selectedSmallSlideIndex}
          slidesPerView={slidesPerView}
          classes={{ dots: classes.dots }}
          mainPhotoSrcSetConfig={[{ minWidth: 0, preset: 'p_main' }]}
          onSlideChange={onSlideChange}
          onSlideClick={onSlideClick}
          onMainImageClick={() => setLightBoxIsOpen(true)}
          withDots
          withThumbnails
        />
      </div>

      {lightBoxIsOpen && (
        <Modal
          title={<HtmlPreview htmlText={mainTitle} />}
          controlledIsOpen={lightBoxIsOpen}
          onOpenChange={setLightBoxIsOpen}
          size={Modal.size.fullscreen}
          withoutScaleAnimation
          hideWhenClickOutside
        >
          <div className={classes.modalContent}>
            <PhotoSlider
              classes={{ dots: classes.dots, arrow: classes.arrow }}
              mainPhotoPreset="p_full_960"
              productTypeId={productTypeId}
              photos={photos}
              selectedSlideIndex={selectedSlideIndex}
              selectedSmallSlideIndex={selectedSmallSlideIndex}
              slidesPerView={slidesPerView}
              mainPhotoSrcSetConfig={[
                { minWidth: 1500, preset: 'p_full_1440' },
                { minWidth: 992, preset: 'p_full_960' },
                { minWidth: 0, preset: 'p_full_480' },
              ]}
              onSlideChange={onSlideChange}
              onSlideClick={onSlideClick}
              withDots
              withThumbnails
              withArrows
              wide
            />
          </div>
        </Modal>
      )}
    </div>
  );
};

export default memo(ProductPhoto);
